// Settings → Wizard Branding tab.
//
// Source-of-truth render lives in portal-standalone.html:
//   • CSS:                 34731-34864 (wb- styles)
//   • Settings IIFE:       34865-35341 (panel HTML, fetch/save, live preview)
//   • Tab injection:       35304-35326 (runtime add_tab patch)
//
// Spec — TKOS_MASTER_CONTEXT.md §SETTINGS / Wizard Branding; inventory
// 01_retailer.md §8. Master spec asks for: accent colour, logo override,
// welcome text, submit-button label, privacy disclaimer, copyable embed
// snippet, and a styled live preview that mimics the wizard's intro
// screen (no real iframe).
//
// Backend wiring
// --------------
// kitchenos-api/modules/wizard_branding.js exposes:
//   GET  /api/wizard/branding/:retailerId   — public, returns saved cfg
//                                             or DEFAULTS
//   POST /api/wizard/branding               — requires kos_portal cookie
// Both are mounted at /api/wizard by server.js:2275-2279.
//
// We keep this tab's data model a superset of the master-doc field set
// AND the on-disk schema (primary, secondary, text, fontFamily,
// buttonStyle, welcomeHeading, welcomeSubtext, showPoweredBy, logoUrl)
// so saved configs round-trip cleanly with the existing wizard.

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import type { SettingsTab, TabCleanup } from "../types";
import type { PageContext } from "../../../shell/types";

import "./wizard-branding.css";

// ── Types ────────────────────────────────────────────────────────────────────

type ButtonStyle = "rounded" | "square" | "pill";

interface BrandingConfig {
  primary:           string;   // hex
  secondary:         string;   // hex
  text:              string;   // hex
  logoUrl:           string;   // url or data: uri (optional)
  fontFamily:        string;
  buttonStyle:       ButtonStyle;
  welcomeHeading:    string;
  welcomeSubtext:    string;
  showPoweredBy:     boolean;
  submitLabel:       string;   // master-spec field; backend stores in
                               // welcomeSubtext until DEFAULTS extend
  privacyDisclaimer: string;   // master-spec field; not yet persisted
                               // by wizard_branding.js — round-tripped
                               // locally only

  // ── Wizard copy (2026-08-10 redesign) ──────────────────────────────────
  // NARRATIVE copy only. Form field labels stay fixed in code — see
  // wizard_branding.js DEFAULTS for why.
  splashHeadline:    string;
  splashCtaLabel:    string;
  reviewHeadline:    string;
  reviewSubhead:     string;
  whatHappensNext:   string;

  // Splash hero imagery: 'gallery' pulls from the Style & Colour door-range
  // photography (zero setup), 'custom' uses heroImages[].
  heroImageSource:   HeroImageSource;
  heroImages:        string[];
}

type HeroImageSource = "gallery" | "custom";

interface BrandingResponse {
  retailerId?: string;
  branding?:   Partial<BrandingConfig>;
  isDefault?:  boolean;
}

const DEFAULTS: Readonly<BrandingConfig> = Object.freeze({
  primary:           "#f97316",
  secondary:         "#1a1a1c",
  text:              "#ffffff",
  logoUrl:           "",
  fontFamily:        "Inter",
  buttonStyle:       "rounded",
  welcomeHeading:    "Design your dream kitchen in 90 seconds",
  welcomeSubtext:
    "Answer a few quick questions and we'll have a designer call you back with ideas, prices and lead times.",
  showPoweredBy:     true,
  submitLabel:       "Get my free design quote",
  privacyDisclaimer: "",
  splashHeadline:    "Let's plan your kitchen",
  splashCtaLabel:    "Get started",
  reviewHeadline:    "Thank you — that's everything we need.",
  reviewSubhead:     "Have a check through what you've told us, then submit and your specialist will be in touch within one working day.",
  whatHappensNext:   "We'll create your personal kitchen portal so you can chat with your designer, view photos and approve quotes — all in one place.",
  heroImageSource:   "gallery",
  heroImages:        [],
});

const HERO_SOURCES: HeroImageSource[] = ["gallery", "custom"];

/** Matches MAX_HERO_IMAGES in kitchenos-api/modules/wizard_branding.js. */
const MAX_HERO_IMAGES = 6;

const ALLOWED_FONTS = [
  "Inter",
  "Poppins",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Inherit from site",
];

const BUTTON_STYLES: ButtonStyle[] = ["rounded", "square", "pill"];

const LS_KEY = "tkos.settings.wizard_branding";
const RETAILER_ID_KEY = "kos_retailer_id"; // matches portal-standalone

// ── Validation ───────────────────────────────────────────────────────────────

function isHex(s: unknown): s is string {
  return typeof s === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(s);
}

function safeUrl(s: unknown): string {
  if (typeof s !== "string") return "";
  const v = s.trim().slice(0, 4000);
  if (!v) return "";
  if (/^(https?:\/\/|data:image\/|\/)/i.test(v)) return v;
  return "";
}

function clampStr(s: unknown, max: number): string {
  if (typeof s !== "string") return "";
  return s.replace(/[\x00-\x08\x0b\x0c\x0e-\x1f]/g, "").trim().slice(0, max);
}

function sanitiseConfig(input: Partial<BrandingConfig> | null | undefined): BrandingConfig {
  const i = input || {};
  return {
    primary:           isHex(i.primary)   ? i.primary   : DEFAULTS.primary,
    secondary:         isHex(i.secondary) ? i.secondary : DEFAULTS.secondary,
    text:              isHex(i.text)      ? i.text      : DEFAULTS.text,
    logoUrl:           safeUrl(i.logoUrl),
    fontFamily:        ALLOWED_FONTS.includes(String(i.fontFamily)) ? String(i.fontFamily) : DEFAULTS.fontFamily,
    buttonStyle:       BUTTON_STYLES.includes(i.buttonStyle as ButtonStyle) ? (i.buttonStyle as ButtonStyle) : DEFAULTS.buttonStyle,
    welcomeHeading:    clampStr(i.welcomeHeading,    160) || DEFAULTS.welcomeHeading,
    welcomeSubtext:    clampStr(i.welcomeSubtext,    400) || DEFAULTS.welcomeSubtext,
    showPoweredBy:     typeof i.showPoweredBy === "boolean" ? i.showPoweredBy : DEFAULTS.showPoweredBy,
    submitLabel:       clampStr(i.submitLabel,       80)  || DEFAULTS.submitLabel,
    privacyDisclaimer: clampStr(i.privacyDisclaimer, 400),
    splashHeadline:    clampStr(i.splashHeadline,   160) || DEFAULTS.splashHeadline,
    splashCtaLabel:    clampStr(i.splashCtaLabel,    60) || DEFAULTS.splashCtaLabel,
    reviewHeadline:    clampStr(i.reviewHeadline,   160) || DEFAULTS.reviewHeadline,
    reviewSubhead:     clampStr(i.reviewSubhead,    400) || DEFAULTS.reviewSubhead,
    whatHappensNext:   clampStr(i.whatHappensNext,  400) || DEFAULTS.whatHappensNext,
    heroImageSource:   sanitiseHeroSource(i),
    heroImages:        sanitiseHeroImages(i.heroImages),
  };
}

function sanitiseHeroImages(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return input.map(safeUrl).filter(Boolean).slice(0, MAX_HERO_IMAGES);
}

/** 'custom' with no usable images would render a blank hero — fall back to
 *  the range gallery. Mirrors the same guard on the server. */
function sanitiseHeroSource(i: Partial<BrandingConfig>): HeroImageSource {
  const src = HERO_SOURCES.includes(i.heroImageSource as HeroImageSource)
    ? (i.heroImageSource as HeroImageSource)
    : DEFAULTS.heroImageSource;
  if (src === "custom" && sanitiseHeroImages(i.heroImages).length === 0) return "gallery";
  return src;
}

function radiusFor(style: ButtonStyle): string {
  if (style === "square") return "0px";
  if (style === "pill") return "50px";
  return "8px";
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function getRetailerId(): string {
  try {
    const v = localStorage.getItem(RETAILER_ID_KEY);
    if (v && /^[A-Za-z0-9_-]+$/.test(v)) return v;
  } catch { /* localStorage unavailable */ }
  return "demo";
}

// ── Persistence ──────────────────────────────────────────────────────────────

function loadLocal(): BrandingConfig {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return sanitiseConfig(JSON.parse(raw) as Partial<BrandingConfig>);
  } catch { /* fall through */ }
  return sanitiseConfig(null);
}

function saveLocal(cfg: BrandingConfig): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(cfg)); } catch { /* non-fatal */ }
}

async function fetchRemote(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<BrandingConfig | null> {
  const rid = getRetailerId();
  try {
    const res = await ctx.api<BrandingResponse>(
      `/wizard/branding/${encodeURIComponent(rid)}`,
      { signal },
    );
    if (res?.branding) return sanitiseConfig(res.branding);
    return null;
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      return null;
    }
    throw err;
  }
}

async function saveRemote(
  ctx: PageContext,
  cfg: BrandingConfig,
  signal: AbortSignal,
): Promise<{ remote: boolean }> {
  const rid = getRetailerId();
  // Brand identity (primary/secondary/logoUrl) is owned by Settings → Branding
  // (PG) and is ignored by the wizard branding read path, so we never persist
  // it into the wizard file store — this tab saves CONTENT only. 2026-06-10.
  const { primary: _p, secondary: _s, logoUrl: _l, ...content } = cfg;
  void _p; void _s; void _l;
  // Persist straight to the live wizard-branding route. (We used to probe a
  // never-implemented "/portal/settings/wizard-branding" first, which always
  // 404'd and showed up as a misleading error in the Network tab before
  // falling through here — removed 2026-06-10.)
  try {
    await ctx.api<unknown>("/wizard/branding", {
      method: "POST",
      body: JSON.stringify({ retailerId: rid, ...content }),
      signal,
    });
    return { remote: true };
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      return { remote: false };
    }
    throw err;
  }
}

// ── Render ───────────────────────────────────────────────────────────────────

function wizardOrigin(): string {
  // The wizard is served from its OWN host (dev.tkos.io / WIZARD_BASE_URL), NOT
  // from wherever the retailer portal happens to be served. Using
  // window.location.origin produced https://portal.tkos.io/start/... — a host
  // that doesn't serve the wizard — so the shareable link and embed snippet
  // were wrong. Read the build-time wizard base, falling back to dev. (2026-06-10)
  const base = (import.meta as any)?.env?.VITE_WIZARD_BASE_URL || "https://dev.tkos.io";
  return String(base).replace(/\/+$/, "");
}

function buildEmbedSnippet(retailerId: string): string {
  // Master-spec example: <script src="https://wizard.thekitchenos.io/embed.js" data-retailer-id="..."></script>
  // Mirrors the on-disk wizard-embed.js mount pattern used in the
  // portal-standalone wizard-branding panel.
  return (
    `<div id="kos-wizard" data-retailer="${retailerId}"></div>\n` +
    `<script src="${wizardOrigin()}/wizard-embed.js" async></script>`
  );
}

/** Shareable direct link to the retailer's branded wizard. The wizard parses
 *  /start/:retailerId (see apps/wizard/src/main.ts) and pre-loads branding. */
function buildWizardLink(retailerId: string): string {
  return `${wizardOrigin()}/start/${encodeURIComponent(retailerId)}`;
}

type ButtonSize = "small" | "medium" | "large";
type ButtonShape = "rounded" | "square" | "pill";

interface ButtonMakerState {
  text: string;
  color: string;
  size: ButtonSize;
  shape: ButtonShape;
}

function buttonSizeCss(size: ButtonSize): { padding: string; fontSize: string } {
  if (size === "small") return { padding: "8px 16px", fontSize: "14px" };
  if (size === "large") return { padding: "16px 30px", fontSize: "18px" };
  return { padding: "12px 22px", fontSize: "16px" };
}

function buttonShapeRadius(shape: ButtonShape): string {
  if (shape === "square") return "0";
  if (shape === "pill") return "999px";
  return "10px";
}

/** Copy-paste HTML for a styled link button that opens the retailer's wizard. */
function buildButtonSnippet(bm: ButtonMakerState, link: string): string {
  const { padding, fontSize } = buttonSizeCss(bm.size);
  const radius = buttonShapeRadius(bm.shape);
  const text = bm.text.trim() || "Design your kitchen";
  return (
    `<a href="${escapeHtml(link)}" target="_blank" rel="noopener" ` +
    `style="display:inline-block;background:${escapeHtml(bm.color)};color:#ffffff;` +
    `text-decoration:none;font-weight:700;font-family:Inter,system-ui,sans-serif;` +
    `padding:${padding};font-size:${fontSize};border-radius:${radius};">` +
    `${escapeHtml(text)}</a>`
  );
}

function previewHtml(cfg: BrandingConfig): string {
  const radius = radiusFor(cfg.buttonStyle);
  const fontStack =
    cfg.fontFamily === "Inherit from site"
      ? "inherit"
      : `'${cfg.fontFamily}', system-ui, sans-serif`;
  return `
    <div class="set-wb-preview"
         style="--p:${escapeHtml(cfg.primary)};--s:${escapeHtml(cfg.secondary)};--t:${escapeHtml(cfg.text)};--r:${radius};--f:${fontStack}">
      <div class="set-wb-preview-bar">
        <div class="set-wb-pb-logo">
          ${cfg.logoUrl ? `<img src="${escapeHtml(cfg.logoUrl)}" alt="" data-role="logo">` : ""}
          <span>Your kitchen showroom</span>
        </div>
        <div class="set-wb-pb-step">Step 1 / 5</div>
      </div>
      <div class="set-wb-preview-body">
        <h4 data-role="heading">${escapeHtml(cfg.welcomeHeading)}</h4>
        <p class="set-wb-preview-sub" data-role="subtext">${escapeHtml(cfg.welcomeSubtext)}</p>
        <div class="set-wb-preview-cards">
          <div class="set-wb-preview-card"><b>£18,000</b>Your est. budget</div>
          <div class="set-wb-preview-card"><b>4–6 weeks</b>Estimated delivery</div>
        </div>
        <button type="button" class="set-wb-preview-cta" data-role="cta">${escapeHtml(cfg.submitLabel)}</button>
        <p class="set-wb-preview-disclaimer" data-role="disclaimer"
           ${cfg.privacyDisclaimer ? "" : "hidden"}>${escapeHtml(cfg.privacyDisclaimer)}</p>
      </div>
      <!-- Must mirror what the wizard actually renders — see
           apps/wizard/src/wizard/index.ts (the "Powered by" credit). If that
           string changes, change it here too, or this preview lies to the
           retailer about what their customers see. -->
      <div class="set-wb-preview-pwd" data-role="pwd" ${cfg.showPoweredBy ? "" : "hidden"}>Powered by Kitchengine</div>
    </div>
  `;
}

const tab: SettingsTab = {
  id: "wizard-branding",
  label: "Wizard Content",
  icon: iconMarkup("sparkle", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    let state: BrandingConfig = loadLocal();
    const controller = new AbortController();
    let disposed = false;
    const objectUrls: string[] = []; // for cleanup of FileReader-derived blobs

    // Prefer the public slug so the wizard link + embed snippet never expose the
    // internal retailer UUID. Falls back to the UUID (which the wizard backend
    // now resolves anyway) then the local id.
    const rid = ctx.user?.retailerSlug || ctx.user?.retailerId || getRetailerId();
    const embedSnippet = buildEmbedSnippet(rid);
    const wizardLink = buildWizardLink(rid);
    const bm: ButtonMakerState = { text: "Design your kitchen", color: state.primary, size: "medium", shape: "rounded" };

    const fontOptions = ALLOWED_FONTS.map(
      (f) => `<option value="${escapeHtml(f)}"${f === state.fontFamily ? " selected" : ""}>${escapeHtml(f)}</option>`,
    ).join("");

    const buttonStyleOpts = BUTTON_STYLES.map(
      (s) =>
        `<button type="button" class="set-wb-btnstyle ${s}${s === state.buttonStyle ? " is-active" : ""}" data-style="${s}" aria-pressed="${s === state.buttonStyle}">
          <span class="set-wb-bs-preview"></span>
          <span>${s.charAt(0).toUpperCase()}${s.slice(1)}</span>
        </button>`,
    ).join("");

    container.innerHTML = `
      <section class="set-wb-root" aria-labelledby="set-wb-title">
        <header class="set-wb-header">
          <h2 id="set-wb-title" class="set-section-title">Wizard content &amp; presentation</h2>
          <p class="set-help">Customise the copy and presentation of the lead-generation wizard. Your brand colour and logo are set once in Settings → Branding and applied here automatically.</p>
        </header>

        <div class="set-wb-grid">
          <div class="set-wb-form">
            <!-- Brand identity — read-only, inherited from Settings → Branding -->
            <div class="set-field">
              <label>Brand colour &amp; logo</label>
              <div class="set-wb-inherited">
                <span class="set-wb-inherited-swatch" data-role="inherited-swatch" aria-hidden="true"></span>
                <code class="set-wb-inherited-hex" data-role="inherited-hex">—</code>
              </div>
              <p class="set-help">Brand colour inherited from <strong>Settings → Branding</strong>. One colour applies everywhere — your portal, customer portal, wizard, emails and website widgets. Change it there.</p>
            </div>

            <!-- Welcome text -->
            <div class="set-field">
              <label for="set-wb-heading">Welcome heading</label>
              <input id="set-wb-heading" class="set-input" type="text" maxlength="160"
                     value="${escapeHtml(state.welcomeHeading)}" />
            </div>

            <div class="set-field">
              <label for="set-wb-subtext">Welcome text</label>
              <textarea id="set-wb-subtext" class="set-textarea" maxlength="400">${escapeHtml(state.welcomeSubtext)}</textarea>
            </div>

            <!-- Submit label -->
            <div class="set-field">
              <label for="set-wb-submit">Submit button label</label>
              <input id="set-wb-submit" class="set-input" type="text" maxlength="80"
                     value="${escapeHtml(state.submitLabel)}" />
            </div>

            <!-- ── Wizard copy (2026-08-10 redesign) ──────────────────── -->
            <div class="set-wb-subhead">
              <h3 class="set-section-title">Wizard copy</h3>
              <p class="set-help">The words your customers read at the start and end of the wizard. Field labels like "First name" and "Postcode" stay fixed so they always match what we store. Leave any box empty to fall back to the default wording.</p>
            </div>

            <div class="set-field">
              <label for="set-wb-splash-head">Opening headline</label>
              <input id="set-wb-splash-head" class="set-input" type="text" maxlength="160"
                     placeholder="${escapeHtml(DEFAULTS.splashHeadline)}"
                     value="${escapeHtml(state.splashHeadline)}" />
            </div>

            <div class="set-field">
              <label for="set-wb-splash-cta">Opening button label</label>
              <input id="set-wb-splash-cta" class="set-input" type="text" maxlength="60"
                     placeholder="${escapeHtml(DEFAULTS.splashCtaLabel)}"
                     value="${escapeHtml(state.splashCtaLabel)}" />
            </div>

            <div class="set-field">
              <label for="set-wb-review-head">Review page headline</label>
              <input id="set-wb-review-head" class="set-input" type="text" maxlength="160"
                     placeholder="${escapeHtml(DEFAULTS.reviewHeadline)}"
                     value="${escapeHtml(state.reviewHeadline)}" />
            </div>

            <div class="set-field">
              <label for="set-wb-review-sub">Review page subheading</label>
              <textarea id="set-wb-review-sub" class="set-textarea" maxlength="400"
                        placeholder="${escapeHtml(DEFAULTS.reviewSubhead)}">${escapeHtml(state.reviewSubhead)}</textarea>
            </div>

            <div class="set-field">
              <label for="set-wb-whn">"What happens next" message</label>
              <textarea id="set-wb-whn" class="set-textarea" maxlength="400"
                        placeholder="${escapeHtml(DEFAULTS.whatHappensNext)}">${escapeHtml(state.whatHappensNext)}</textarea>
            </div>

            <!-- ── Hero images ────────────────────────────────────────── -->
            <div class="set-wb-subhead">
              <h3 class="set-section-title">Opening image</h3>
              <p class="set-help">The photograph behind your opening headline. By default we rotate photography from the kitchen ranges in your Style &amp; Colour gallery, so there's nothing to set up.</p>
            </div>

            <div class="set-field">
              <div class="set-wb-herosrc" data-role="herosrc" role="radiogroup" aria-label="Opening image source">
                <button type="button" class="set-wb-herosrc-opt${state.heroImageSource === "gallery" ? " is-active" : ""}"
                        data-src="gallery" role="radio" aria-checked="${state.heroImageSource === "gallery"}">
                  <span class="set-wb-herosrc-title">Use my range gallery</span>
                  <span class="set-wb-herosrc-hint">Photography from your kitchen ranges</span>
                </button>
                <button type="button" class="set-wb-herosrc-opt${state.heroImageSource === "custom" ? " is-active" : ""}"
                        data-src="custom" role="radio" aria-checked="${state.heroImageSource === "custom"}">
                  <span class="set-wb-herosrc-title">Use my own images</span>
                  <span class="set-wb-herosrc-hint">Upload up to ${MAX_HERO_IMAGES} photographs</span>
                </button>
              </div>
            </div>

            <!-- Gallery picker: pick specific ranges, or leave all unpicked for every range -->
            <div class="set-field" data-role="hero-gallery-field"${state.heroImageSource === "gallery" ? "" : " hidden"}>
              <label>Choose which range photographs to show</label>
              <p class="set-help">Leave everything unselected to rotate through all of them.</p>
              <div class="set-wb-gallery" data-role="hero-gallery">
                <p class="set-help" data-role="hero-gallery-status">Loading your ranges…</p>
              </div>
            </div>

            <!-- Custom uploads -->
            <div class="set-field" data-role="hero-custom-field"${state.heroImageSource === "custom" ? "" : " hidden"}>
              <label for="set-wb-hero-file">Your images</label>
              <input id="set-wb-hero-file" class="set-input" type="file" accept="image/*" multiple />
              <p class="set-help">JPEG, PNG or WebP, up to 2 MB each. Landscape images work best.</p>
              <div class="set-wb-gallery" data-role="hero-custom-list"></div>
            </div>

            <!-- Typography & button shape -->
            <div class="set-row">
              <div class="set-field">
                <label for="set-wb-font">Font family</label>
                <select id="set-wb-font" class="set-select">${fontOptions}</select>
              </div>
              <div class="set-field">
                <label>Button style</label>
                <div class="set-wb-btnstyles" data-role="btnstyles">${buttonStyleOpts}</div>
              </div>
            </div>

            <!-- Privacy disclaimer -->
            <div class="set-field">
              <label for="set-wb-disclaimer">Privacy disclaimer <span class="set-wb-optional">(optional)</span></label>
              <textarea id="set-wb-disclaimer" class="set-textarea" maxlength="400"
                        placeholder="e.g. By submitting you agree to our privacy policy.">${escapeHtml(state.privacyDisclaimer)}</textarea>
            </div>

            <!-- Powered-by toggle -->
            <div class="set-field">
              <label class="set-wb-checkbox-row">
                <input id="set-wb-pwd" type="checkbox" ${state.showPoweredBy ? "checked" : ""} />
                <span>Show "Powered by Kitchengine"</span>
              </label>
            </div>

            <!-- Action bar -->
            <div class="set-wb-actions">
              <button type="button" class="set-btn" data-role="save">Save</button>
              <button type="button" class="set-btn set-btn-secondary" data-role="reset">Reset to defaults</button>
            </div>
          </div>

          <!-- Live preview -->
          <aside class="set-wb-preview-wrap" aria-label="Live preview">
            <div class="set-wb-preview-host" data-role="preview">${previewHtml(state)}</div>
          </aside>
        </div>

        <!-- Direct wizard link -->
        <section class="set-wb-link-section" aria-labelledby="set-wb-link-title">
          <h3 id="set-wb-link-title" class="set-section-title">Your wizard link</h3>
          <p class="set-help">Share this link in emails, WhatsApp, your website or your email signature. It opens the wizard pre-loaded with your branding.</p>
          <div class="set-wb-link-row">
            <input class="set-input" type="text" readonly data-role="wizard-link" value="${escapeHtml(wizardLink)}" />
            <button type="button" class="set-btn set-btn-secondary" data-role="copy-link">Copy link</button>
          </div>
        </section>

        <!-- Embed snippet -->
        <section class="set-wb-embed-section" aria-labelledby="set-wb-embed-title">
          <h3 id="set-wb-embed-title" class="set-section-title">Embed code</h3>
          <p class="set-help">Paste this snippet anywhere on your site to mount the wizard. It themes itself with the settings above automatically. Your retailer ID is already filled in.</p>
          <pre class="set-wb-embed" data-role="embed-code">${escapeHtml(embedSnippet)}</pre>
          <button type="button" class="set-btn set-btn-secondary" data-role="copy-embed">Copy embed code</button>
        </section>

        <!-- Button maker -->
        <section class="set-wb-btnmaker" aria-labelledby="set-wb-bm-title">
          <h3 id="set-wb-bm-title" class="set-section-title">Website button maker</h3>
          <p class="set-help">Build a button for your website, emails or signature. It links straight to your branded wizard.</p>
          <div class="set-wb-bm-grid">
            <div class="set-wb-bm-controls">
              <div class="set-field">
                <label for="set-wb-bm-text">Button text</label>
                <input id="set-wb-bm-text" class="set-input" type="text" maxlength="40" value="Design your kitchen" />
              </div>
              <div class="set-field">
                <label>Button colour</label>
                <div class="set-wb-inherited">
                  <span class="set-wb-inherited-swatch" data-role="bm-inherited-swatch" aria-hidden="true"></span>
                  <code class="set-wb-inherited-hex" data-role="bm-inherited-hex">—</code>
                </div>
                <p class="set-help">Uses your brand colour from Settings → Branding.</p>
              </div>
              <div class="set-row">
                <div class="set-field">
                  <label for="set-wb-bm-size">Size</label>
                  <select id="set-wb-bm-size" class="set-select">
                    <option value="small">Small</option>
                    <option value="medium" selected>Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>
                <div class="set-field">
                  <label for="set-wb-bm-style">Style</label>
                  <select id="set-wb-bm-style" class="set-select">
                    <option value="rounded" selected>Rounded</option>
                    <option value="square">Square</option>
                    <option value="pill">Pill</option>
                  </select>
                </div>
              </div>
            </div>
            <aside class="set-wb-bm-preview-wrap" aria-label="Button preview">
              <span class="set-help">Live preview</span>
              <div class="set-wb-bm-preview" data-role="bm-preview"></div>
            </aside>
          </div>
          <pre class="set-wb-embed" data-role="bm-code"></pre>
          <button type="button" class="set-btn set-btn-secondary" data-role="copy-button">Copy button code</button>
        </section>
      </section>
    `;

    // ── Element handles ─────────────────────────────────────────────────
    // Brand colour + logo are inherited (read-only) from Settings → Branding,
    // so there are no accent/logo inputs here any more — just the read-only
    // swatch + hex display that mirrors the resolved PG brand colour.
    const inheritedSwatch   = container.querySelector<HTMLElement>("[data-role='inherited-swatch']")!;
    const inheritedHex      = container.querySelector<HTMLElement>("[data-role='inherited-hex']")!;
    const bmInheritedSwatch = container.querySelector<HTMLElement>("[data-role='bm-inherited-swatch']")!;
    const bmInheritedHex    = container.querySelector<HTMLElement>("[data-role='bm-inherited-hex']")!;
    const headingEl     = container.querySelector<HTMLInputElement>("#set-wb-heading")!;
    const subtextEl     = container.querySelector<HTMLTextAreaElement>("#set-wb-subtext")!;
    const submitEl      = container.querySelector<HTMLInputElement>("#set-wb-submit")!;
    const fontEl        = container.querySelector<HTMLSelectElement>("#set-wb-font")!;
    const btnStylesEl   = container.querySelector<HTMLElement>("[data-role='btnstyles']")!;
    const disclaimerEl  = container.querySelector<HTMLTextAreaElement>("#set-wb-disclaimer")!;
    const pwdEl         = container.querySelector<HTMLInputElement>("#set-wb-pwd")!;
    const previewHost   = container.querySelector<HTMLElement>("[data-role='preview']")!;
    const embedCodeEl   = container.querySelector<HTMLPreElement>("[data-role='embed-code']")!;
    const copyEmbedBtn  = container.querySelector<HTMLButtonElement>("[data-role='copy-embed']")!;
    const saveBtn       = container.querySelector<HTMLButtonElement>("[data-role='save']")!;
    const resetBtn      = container.querySelector<HTMLButtonElement>("[data-role='reset']")!;
    const linkInput     = container.querySelector<HTMLInputElement>("[data-role='wizard-link']")!;
    const copyLinkBtn   = container.querySelector<HTMLButtonElement>("[data-role='copy-link']")!;
    const bmText        = container.querySelector<HTMLInputElement>("#set-wb-bm-text")!;
    const bmSize        = container.querySelector<HTMLSelectElement>("#set-wb-bm-size")!;
    const bmStyle       = container.querySelector<HTMLSelectElement>("#set-wb-bm-style")!;
    const bmPreview     = container.querySelector<HTMLElement>("[data-role='bm-preview']")!;
    const bmCode        = container.querySelector<HTMLPreElement>("[data-role='bm-code']")!;
    const copyButtonBtn = container.querySelector<HTMLButtonElement>("[data-role='copy-button']")!;
    const splashHeadEl  = container.querySelector<HTMLInputElement>("#set-wb-splash-head")!;
    const splashCtaEl   = container.querySelector<HTMLInputElement>("#set-wb-splash-cta")!;
    const reviewHeadEl  = container.querySelector<HTMLInputElement>("#set-wb-review-head")!;
    const reviewSubEl   = container.querySelector<HTMLTextAreaElement>("#set-wb-review-sub")!;
    const whnEl         = container.querySelector<HTMLTextAreaElement>("#set-wb-whn")!;
    const heroSrcEl     = container.querySelector<HTMLElement>("[data-role='herosrc']")!;
    const heroGalField  = container.querySelector<HTMLElement>("[data-role='hero-gallery-field']")!;
    const heroGalEl     = container.querySelector<HTMLElement>("[data-role='hero-gallery']")!;
    const heroCusField  = container.querySelector<HTMLElement>("[data-role='hero-custom-field']")!;
    const heroFileEl    = container.querySelector<HTMLInputElement>("#set-wb-hero-file")!;
    const heroCusListEl = container.querySelector<HTMLElement>("[data-role='hero-custom-list']")!;

    // ── Live preview update ─────────────────────────────────────────────
    const refreshPreview = (): void => {
      const host = previewHost.querySelector<HTMLElement>(".set-wb-preview");
      if (!host) {
        previewHost.innerHTML = previewHtml(state);
        return;
      }
      host.style.setProperty("--p", state.primary);
      host.style.setProperty("--s", state.secondary);
      host.style.setProperty("--t", state.text);
      host.style.setProperty("--r", radiusFor(state.buttonStyle));
      host.style.setProperty(
        "--f",
        state.fontFamily === "Inherit from site"
          ? "inherit"
          : `'${state.fontFamily}', system-ui, sans-serif`,
      );
      const heading = host.querySelector<HTMLElement>("[data-role='heading']");
      if (heading) heading.textContent = state.welcomeHeading;
      const sub = host.querySelector<HTMLElement>("[data-role='subtext']");
      if (sub) sub.textContent = state.welcomeSubtext;
      const cta = host.querySelector<HTMLElement>("[data-role='cta']");
      if (cta) cta.textContent = state.submitLabel;
      const disc = host.querySelector<HTMLElement>("[data-role='disclaimer']");
      if (disc) {
        disc.textContent = state.privacyDisclaimer;
        if (state.privacyDisclaimer) disc.removeAttribute("hidden");
        else disc.setAttribute("hidden", "");
      }
      const pwd = host.querySelector<HTMLElement>("[data-role='pwd']");
      if (pwd) {
        if (state.showPoweredBy) pwd.removeAttribute("hidden");
        else pwd.setAttribute("hidden", "");
      }
      // Logo
      const logoBar = host.querySelector<HTMLElement>(".set-wb-pb-logo");
      if (logoBar) {
        const existing = logoBar.querySelector<HTMLImageElement>("img[data-role='logo']");
        if (state.logoUrl) {
          if (existing) {
            if (existing.src !== state.logoUrl) existing.src = state.logoUrl;
          } else {
            const img = document.createElement("img");
            img.alt = "";
            img.dataset.role = "logo";
            img.src = state.logoUrl;
            logoBar.insertBefore(img, logoBar.firstChild);
          }
        } else if (existing) {
          existing.remove();
        }
      }
    };

    // ── Inherited brand colour (read-only mirror of Settings → Branding) ──
    // The wizard, button maker and live preview all use the retailer's single
    // brand colour resolved from PG (via GET /api/wizard/branding, which now
    // sources identity solely from `retailers`). This paints the read-only
    // swatches and keeps the button-maker colour pointed at PG. 2026-06-10.
    const updateInherited = (): void => {
      const c = isHex(state.primary) ? state.primary : "#E8760A";
      const hex = c.toUpperCase();
      inheritedSwatch.style.background = c;
      inheritedHex.textContent = hex;
      bmInheritedSwatch.style.background = c;
      bmInheritedHex.textContent = hex;
      bm.color = c; // button creator follows the inherited PG brand colour
    };

    // ── Form ↔ state syncing ────────────────────────────────────────────
    const onHeading = (): void => {
      state.welcomeHeading = clampStr(headingEl.value, 160) || DEFAULTS.welcomeHeading;
      saveLocal(state);
      refreshPreview();
    };

    const onSubtext = (): void => {
      state.welcomeSubtext = clampStr(subtextEl.value, 400) || DEFAULTS.welcomeSubtext;
      saveLocal(state);
      refreshPreview();
    };

    const onSubmit = (): void => {
      state.submitLabel = clampStr(submitEl.value, 80) || DEFAULTS.submitLabel;
      saveLocal(state);
      refreshPreview();
    };

    const onFont = (): void => {
      const v = fontEl.value;
      if (ALLOWED_FONTS.includes(v)) {
        state.fontFamily = v;
        saveLocal(state);
        refreshPreview();
      }
    };

    const onButtonStyle = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-style]");
      if (!btn) return;
      const v = btn.getAttribute("data-style") as ButtonStyle | null;
      if (!v || !BUTTON_STYLES.includes(v)) return;
      state.buttonStyle = v;
      btnStylesEl.querySelectorAll<HTMLButtonElement>("button[data-style]").forEach((b) => {
        const active = b.getAttribute("data-style") === v;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-pressed", String(active));
      });
      saveLocal(state);
      refreshPreview();
    };

    const onDisclaimer = (): void => {
      state.privacyDisclaimer = clampStr(disclaimerEl.value, 400);
      saveLocal(state);
      refreshPreview();
    };

    const onPwd = (): void => {
      state.showPoweredBy = pwdEl.checked;
      saveLocal(state);
      refreshPreview();
    };

    const copyText = async (text: string, okLabel: string): Promise<void> => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          ctx.showToast(okLabel, "success");
          return;
        }
      } catch { /* fall through to fallback */ }
      // Fallback for browsers without clipboard API.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        ctx.showToast(okLabel, "success");
      } catch {
        ctx.showToast("Copy failed — please copy manually", "error");
      } finally {
        ta.remove();
      }
    };

    const onCopyEmbed = (): Promise<void> => copyText(embedCodeEl.textContent ?? "", "Embed code copied");
    const onCopyLink  = (): Promise<void> => copyText(linkInput.value, "Wizard link copied");
    const onCopyButton = (): Promise<void> => copyText(bmCode.textContent ?? "", "Button code copied");

    // ── Button maker: live preview + generated snippet ──────────────────────
    const refreshButtonMaker = (): void => {
      const snippet = buildButtonSnippet(bm, wizardLink);
      bmCode.textContent = snippet;
      // Preview the actual rendered button (sanitised — we built the HTML).
      bmPreview.innerHTML = snippet;
    };
    const onBmText = (): void => { bm.text = bmText.value; refreshButtonMaker(); };
    const onBmSize = (): void => { bm.size = bmSize.value as ButtonSize; refreshButtonMaker(); };
    const onBmStyle = (): void => { bm.shape = bmStyle.value as ButtonShape; refreshButtonMaker(); };

    // ── Wizard copy ─────────────────────────────────────────────────────
    // Each field falls back to the shipped default when cleared, so a retailer
    // can never leave a headline blank on a live surface.
    type CopyKey = "splashHeadline" | "splashCtaLabel" | "reviewHeadline" | "reviewSubhead" | "whatHappensNext";
    const copyFields: Array<{ el: HTMLInputElement | HTMLTextAreaElement; key: CopyKey; max: number }> = [
      { el: splashHeadEl, key: "splashHeadline",  max: 160 },
      { el: splashCtaEl,  key: "splashCtaLabel",  max: 60  },
      { el: reviewHeadEl, key: "reviewHeadline",  max: 160 },
      { el: reviewSubEl,  key: "reviewSubhead",   max: 400 },
      { el: whnEl,        key: "whatHappensNext", max: 400 },
    ];
    const onCopyField = (f: (typeof copyFields)[number]) => (): void => {
      state[f.key] = clampStr(f.el.value, f.max) || DEFAULTS[f.key];
      saveLocal(state);
    };
    const copyHandlers = copyFields.map((f) => ({ f, h: onCopyField(f) }));

    // ── Hero images ─────────────────────────────────────────────────────
    // heroImages[] serves BOTH modes: in 'gallery' it's the retailer's chosen
    // subset of range photography (empty = rotate all of them); in 'custom'
    // it's their uploads. Switching mode therefore clears the list, otherwise
    // uploads would silently reappear as "selected" gallery tiles.
    let galleryImages: Array<{ url: string; label: string }> = [];

    const renderHeroCustomList = (): void => {
      if (state.heroImages.length === 0) {
        heroCusListEl.innerHTML = `<p class="set-help">No images yet.</p>`;
        return;
      }
      heroCusListEl.innerHTML = state.heroImages
        .map(
          (url, idx) => `
          <div class="set-wb-gallery-item">
            <img src="${escapeHtml(url)}" alt="" loading="lazy" />
            <button type="button" class="set-wb-gallery-remove" data-remove="${idx}"
                    aria-label="Remove image ${idx + 1}">&times;</button>
          </div>`,
        )
        .join("");
    };

    const renderHeroGallery = (): void => {
      if (galleryImages.length === 0) {
        heroGalEl.innerHTML =
          `<p class="set-help">No range photography found yet. Add images to your kitchen ranges, or switch to "Use my own images" above.</p>`;
        return;
      }
      heroGalEl.innerHTML = galleryImages
        .map(({ url, label }) => {
          const on = state.heroImages.includes(url);
          return `
          <button type="button" class="set-wb-gallery-item set-wb-gallery-pick${on ? " is-active" : ""}"
                  data-pick="${escapeHtml(url)}" aria-pressed="${on}" title="${escapeHtml(label)}">
            <img src="${escapeHtml(url)}" alt="${escapeHtml(label)}" loading="lazy" />
            <span class="set-wb-gallery-label">${escapeHtml(label)}</span>
          </button>`;
        })
        .join("");
    };

    const onHeroSrc = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-src]");
      if (!btn) return;
      const v = btn.getAttribute("data-src") as HeroImageSource | null;
      if (!v || !HERO_SOURCES.includes(v) || v === state.heroImageSource) return;
      state.heroImageSource = v;
      state.heroImages = [];
      heroSrcEl.querySelectorAll<HTMLButtonElement>("button[data-src]").forEach((b) => {
        const active = b.getAttribute("data-src") === v;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-checked", String(active));
      });
      heroGalField.hidden = v !== "gallery";
      heroCusField.hidden = v !== "custom";
      renderHeroGallery();
      renderHeroCustomList();
      saveLocal(state);
    };

    const onHeroPick = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-pick]");
      if (!btn) return;
      const url = btn.getAttribute("data-pick") || "";
      if (!url) return;
      const at = state.heroImages.indexOf(url);
      if (at >= 0) state.heroImages.splice(at, 1);
      else if (state.heroImages.length >= MAX_HERO_IMAGES) {
        ctx.showToast(`Pick up to ${MAX_HERO_IMAGES} images`, "info");
        return;
      } else state.heroImages.push(url);
      saveLocal(state);
      renderHeroGallery();
    };

    const onHeroRemove = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-remove]");
      if (!btn) return;
      const idx = Number(btn.getAttribute("data-remove"));
      if (!Number.isInteger(idx)) return;
      state.heroImages.splice(idx, 1);
      saveLocal(state);
      renderHeroCustomList();
    };

    const HERO_MAX_BYTES = 2 * 1024 * 1024;
    const onHeroFile = (): void => {
      const files = Array.from(heroFileEl.files || []);
      heroFileEl.value = ""; // allow re-picking the same file after a removal
      if (files.length === 0) return;
      const room = MAX_HERO_IMAGES - state.heroImages.length;
      if (room <= 0) {
        ctx.showToast(`Up to ${MAX_HERO_IMAGES} images — remove one first`, "info");
        return;
      }
      for (const file of files.slice(0, room)) {
        if (!file.type.startsWith("image/")) {
          ctx.showToast(`${file.name} isn't an image`, "error");
          continue;
        }
        if (file.size > HERO_MAX_BYTES) {
          ctx.showToast(`${file.name} is over 2 MB`, "error");
          continue;
        }
        const reader = new FileReader();
        reader.onload = (): void => {
          if (disposed) return;
          const url = safeUrl(reader.result);
          if (!url) return;
          if (state.heroImages.length >= MAX_HERO_IMAGES) return;
          state.heroImages.push(url);
          saveLocal(state);
          renderHeroCustomList();
        };
        reader.readAsDataURL(file);
      }
      if (files.length > room) ctx.showToast(`Only the first ${room} were added`, "info");
    };

    /** Pull hero/portfolio photography off the retailer's door ranges. */
    const loadGallery = async (): Promise<void> => {
      interface RangeRow {
        name?: string;
        heroImage?: string;
        portfolioImages?: Array<{ url?: string; colourName?: string }>;
      }
      try {
        const res = await ctx.api<RangeRow[] | { ranges?: RangeRow[] }>("/door-ranges", {
          signal: controller.signal,
        });
        if (disposed || controller.signal.aborted) return;
        const rows: RangeRow[] = Array.isArray(res) ? res : (res?.ranges ?? []);
        const seen = new Set<string>();
        const out: Array<{ url: string; label: string }> = [];
        for (const r of rows) {
          const label = clampStr(r.name, 60) || "Kitchen range";
          for (const raw of [r.heroImage, ...(r.portfolioImages ?? []).map((p) => p?.url)]) {
            const url = safeUrl(raw);
            if (!url || seen.has(url)) continue;
            seen.add(url);
            out.push({ url, label });
          }
        }
        galleryImages = out;
        renderHeroGallery();
      } catch (err) {
        if (disposed || controller.signal.aborted) return;
        heroGalEl.innerHTML =
          `<p class="set-help">Couldn't load your ranges just now. Your wizard still shows range photography — this picker is only for narrowing it down.</p>`;
        console.warn("[settings/wizard-branding] gallery fetch failed:", err);
      }
    };

    /** Push `state`'s copy + hero values back into the DOM. Used by Reset and
     *  by the remote-config fetch, both of which replace `state` wholesale. */
    const syncCopyAndHeroInputs = (): void => {
      for (const f of copyFields) f.el.value = state[f.key];
      heroSrcEl.querySelectorAll<HTMLButtonElement>("button[data-src]").forEach((b) => {
        const active = b.getAttribute("data-src") === state.heroImageSource;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-checked", String(active));
      });
      heroGalField.hidden = state.heroImageSource !== "gallery";
      heroCusField.hidden = state.heroImageSource !== "custom";
      renderHeroGallery();
      renderHeroCustomList();
    };

    const onSave = async (): Promise<void> => {
      saveBtn.disabled = true;
      const orig = saveBtn.textContent;
      saveBtn.textContent = "Saving…";
      try {
        const { remote } = await saveRemote(ctx, state, controller.signal);
        saveLocal(state);
        ctx.showToast(
          remote ? "Wizard branding saved" : "Saved locally (server route not yet wired)",
          "success",
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't save (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Couldn't save branding";
        ctx.showToast(msg, "error");
      } finally {
        if (!disposed) {
          saveBtn.disabled = false;
          saveBtn.textContent = orig;
        }
      }
    };

    const onReset = (): void => {
      if (!confirm("Reset wizard content to The Kitchen OS defaults?")) return;
      // Preserve the inherited brand identity (colour/secondary/logo come from
      // Settings → Branding, not this tab) — only the wizard CONTENT resets.
      const keepPrimary = state.primary;
      const keepSecondary = state.secondary;
      const keepLogo = state.logoUrl;
      state = sanitiseConfig(null);
      state.primary = keepPrimary;
      state.secondary = keepSecondary;
      state.logoUrl = keepLogo;
      // Re-sync inputs.
      headingEl.value = state.welcomeHeading;
      subtextEl.value = state.welcomeSubtext;
      submitEl.value = state.submitLabel;
      fontEl.value = state.fontFamily;
      disclaimerEl.value = state.privacyDisclaimer;
      pwdEl.checked = state.showPoweredBy;
      btnStylesEl.querySelectorAll<HTMLButtonElement>("button[data-style]").forEach((b) => {
        const active = b.getAttribute("data-style") === state.buttonStyle;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-pressed", String(active));
      });
      syncCopyAndHeroInputs();
      saveLocal(state);
      updateInherited();
      refreshPreview();
      refreshButtonMaker();
      ctx.showToast("Wizard content reset to defaults", "info");
    };

    // ── Wire events ─────────────────────────────────────────────────────
    headingEl.addEventListener("input", onHeading);
    subtextEl.addEventListener("input", onSubtext);
    submitEl.addEventListener("input", onSubmit);
    fontEl.addEventListener("change", onFont);
    btnStylesEl.addEventListener("click", onButtonStyle);
    disclaimerEl.addEventListener("input", onDisclaimer);
    pwdEl.addEventListener("change", onPwd);
    copyEmbedBtn.addEventListener("click", () => { void onCopyEmbed(); });
    copyLinkBtn.addEventListener("click", () => { void onCopyLink(); });
    copyButtonBtn.addEventListener("click", () => { void onCopyButton(); });
    linkInput.addEventListener("focus", () => linkInput.select());
    bmText.addEventListener("input", onBmText);
    bmSize.addEventListener("change", onBmSize);
    bmStyle.addEventListener("change", onBmStyle);
    saveBtn.addEventListener("click", () => { void onSave(); });
    resetBtn.addEventListener("click", onReset);
    for (const { f, h } of copyHandlers) f.el.addEventListener("input", h);
    heroSrcEl.addEventListener("click", onHeroSrc);
    heroGalEl.addEventListener("click", onHeroPick);
    heroCusListEl.addEventListener("click", onHeroRemove);
    heroFileEl.addEventListener("change", onHeroFile);

    // Initial paint — mirror the inherited brand colour, then the button maker.
    updateInherited();
    refreshButtonMaker();
    renderHeroCustomList();
    void loadGallery();

    // ── Pull remote config on mount ─────────────────────────────────────
    void (async () => {
      try {
        const remote = await fetchRemote(ctx, controller.signal);
        if (disposed || controller.signal.aborted) return;
        if (remote) {
          // Server wins, but preserve master-spec-only fields (submitLabel,
          // privacyDisclaimer) from local since the existing wizard
          // backend doesn't yet persist them.
          state = sanitiseConfig({
            ...remote,
            submitLabel:       state.submitLabel,
            privacyDisclaimer: state.privacyDisclaimer,
          });
          // Sync visible inputs (brand colour/logo are read-only inherited).
          headingEl.value = state.welcomeHeading;
          subtextEl.value = state.welcomeSubtext;
          fontEl.value = state.fontFamily;
          pwdEl.checked = state.showPoweredBy;
          btnStylesEl.querySelectorAll<HTMLButtonElement>("button[data-style]").forEach((b) => {
            const active = b.getAttribute("data-style") === state.buttonStyle;
            b.classList.toggle("is-active", active);
            b.setAttribute("aria-pressed", String(active));
          });
          syncCopyAndHeroInputs();
          saveLocal(state);
          updateInherited();
          refreshPreview();
          refreshButtonMaker();
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        // eslint-disable-next-line no-console
        console.warn("[settings/wizard-branding] remote fetch failed:", err);
      }
    })();

    return (): void => {
      disposed = true;
      controller.abort();
      headingEl.removeEventListener("input", onHeading);
      subtextEl.removeEventListener("input", onSubtext);
      submitEl.removeEventListener("input", onSubmit);
      fontEl.removeEventListener("change", onFont);
      btnStylesEl.removeEventListener("click", onButtonStyle);
      disclaimerEl.removeEventListener("input", onDisclaimer);
      pwdEl.removeEventListener("change", onPwd);
      bmText.removeEventListener("input", onBmText);
      bmSize.removeEventListener("change", onBmSize);
      bmStyle.removeEventListener("change", onBmStyle);
      for (const { f, h } of copyHandlers) f.el.removeEventListener("input", h);
      heroSrcEl.removeEventListener("click", onHeroSrc);
      heroGalEl.removeEventListener("click", onHeroPick);
      heroCusListEl.removeEventListener("click", onHeroRemove);
      heroFileEl.removeEventListener("change", onHeroFile);
      // Revoke any object URLs we created (FileReader uses data: URIs so
      // this is normally empty — kept for future-proofing if we switch to
      // URL.createObjectURL).
      for (const u of objectUrls) {
        try { URL.revokeObjectURL(u); } catch { /* ignore */ }
      }
    };
  },
};

export default tab;
